import { encryptionService } from './encryptionService';
import { dataValidationService } from './dataValidationService';
import { simplifiedDriveService } from './googleDriveSimplified';

export interface BackupData {
  version: string;
  timestamp: string;
  userProfile: any;
  aspirations: any[];
  goals: any[];
  lists: any[];
  schedules: any[];
  aiConfig: any;
  completions?: any; // Universal completion data (v2 format)
  completionArchives?: any[]; // Archive index for completion data
  metadata: {
    deviceInfo: string;
    appVersion: string;
    backupMethod: 'manual' | 'automatic';
  };
}

export interface BackupProvider {
  name: string;
  id: string;
  icon: string;
  isConfigured: boolean;
  requiresAuth: boolean;
  supportsAutoBackup: boolean;
}

export interface BackupHistoryEntry {
  id: string;
  timestamp: string;
  providerId: string;
  success: boolean;
  error?: string;
  dataSize: number;
}

class DataBackupService {
  private autoBackupEnabled: boolean = false;
  private lastBackupTime: Date | null = null;
  private autoBackupInterval: number | null = null;

  constructor() {
    this.loadBackupSettings();
    this.initializeProviders();
  }

  // Core backup data preparation
  createBackupData(): BackupData {
    const timestamp = new Date().toISOString();

    return {
      version: '1.0.0',
      timestamp,
      userProfile: this.getStorageItem('godmode_user_profile'),
      aspirations: this.getStorageItem('godmode_aspirations') || [],
      goals: this.getStorageItem('godmode_goals') || [],
      lists: this.getStorageItem('godmode_lists') || [],
      schedules: this.getStorageItem('godmode_list_schedules') || [],
      aiConfig: {
        provider: 'anthropic',
        model: this.getStorageItem('godmode_ai_config')?.model || '',
        enabled: this.getStorageItem('godmode_ai_config')?.enabled ?? true,
        userApiKey: localStorage.getItem('godmode_user_anthropic_key') // Get as plain string, don't parse as JSON
      },
      completions: this.getStorageItem('godmode_universal_completions') || null,
      completionArchives: this.getStorageItem('godmode_completion_archives_index')?.archives || [],
      metadata: {
        deviceInfo: this.getDeviceInfo(),
        appVersion: '1.0.0', // Should come from package.json
        backupMethod: 'manual'
      }
    };
  }

  // Validate and restore backup data
  async restoreFromBackup(backupData: BackupData, password?: string): Promise<void> {
    let processedData = backupData;

    // Handle encrypted data
    if (password && typeof backupData === 'string') {
      try {
        const decryptedData = await encryptionService.decryptDataCombined(backupData as unknown as string, password);
        processedData = JSON.parse(decryptedData);
      } catch (error) {
        throw new Error(`Failed to decrypt backup: ${error instanceof Error ? error.message : 'Invalid password'}`);
      }
    }

    // Validate backup data
    const validation = dataValidationService.validateBackupIntegrity(processedData);
    if (!validation.isValid) {
      throw new Error(`Invalid backup data: ${validation.errors.join(', ')}`);
    }

    // Show warnings if any
    if (validation.warnings.length > 0) {
      console.warn('⚠️ Backup validation warnings:', validation.warnings);
    }

    // Migrate data if needed
    const migration = dataValidationService.migrateBackupData(processedData);
    if (!migration.success) {
      throw new Error(`Data migration failed: ${migration.errors.join(', ')}`);
    }

    if (migration.changes.length > 0) {
      console.log('🔄 Data migration applied:', migration.changes);
      processedData = migration as any; // The migration service returns the migrated data
    }

    // Create restoration point first
    const currentData = this.createBackupData();
    localStorage.setItem('godmode_backup_restore_point', JSON.stringify(currentData));

    try {
      // Restore data
      this.setStorageItem('godmode_user_profile', processedData.userProfile);
      this.setStorageItem('godmode_aspirations', processedData.aspirations);
      this.setStorageItem('godmode_goals', processedData.goals);
      this.setStorageItem('godmode_lists', processedData.lists);
      this.setStorageItem('godmode_list_schedules', processedData.schedules);

      // Handle AI config restoration (support both old and new formats)
      if (processedData.aiConfig) {
        if (typeof processedData.aiConfig === 'object' && processedData.aiConfig.userApiKey) {
          // New format with separate user API key
          this.setStorageItem('godmode_ai_config', {
            provider: 'anthropic',
            model: processedData.aiConfig.model || '',
            enabled: processedData.aiConfig.enabled ?? true
          });
          localStorage.setItem('godmode_user_anthropic_key', processedData.aiConfig.userApiKey);
        } else {
          // Old format - just restore as is
          this.setStorageItem('godmode_ai_config', processedData.aiConfig);
        }
      }

      // Restore completion data if present
      if (processedData.completions) {
        this.setStorageItem('godmode_universal_completions', processedData.completions);
        console.log('📋 Completion data restored from backup');
      }

      // Restore completion archives index if present
      if (processedData.completionArchives && processedData.completionArchives.length > 0) {
        this.setStorageItem('godmode_completion_archives_index', {
          archives: processedData.completionArchives
        });
        console.log('📦 Completion archives index restored from backup');
      }

      this.lastBackupTime = new Date(processedData.timestamp);
      this.saveBackupSettings();

      console.log('✅ Data restored from backup successfully');
    } catch (error) {
      // Rollback on failure
      const restorePoint = this.getStorageItem('godmode_backup_restore_point');
      if (restorePoint) {
        await this.restoreFromBackup(restorePoint);
      }
      throw new Error(`Failed to restore backup: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Get available backup providers
  getAvailableProviders(): BackupProvider[] {
    return [
      {
        name: 'Local Device',
        id: 'local',
        icon: '💾',
        isConfigured: true,
        requiresAuth: false,
        supportsAutoBackup: false
      },
      {
        name: 'Google Drive',
        id: 'google_drive',
        icon: '📁',
        isConfigured: this.isProviderConfigured('google_drive'),
        requiresAuth: true,
        supportsAutoBackup: true
      }
    ];
  }

  // Local device backup (File System Access API)
  async backupToLocalDevice(password?: string): Promise<void> {
    if ('showSaveFilePicker' in window) {
      await this.saveWithFileSystemAPI(password);
    } else {
      // Fallback to download blob
      this.downloadBackupFile(password);
    }
  }

  private async saveWithFileSystemAPI(password?: string): Promise<void> {
    try {
      const backupData = this.createBackupData();

      // Sanitize backup data to remove sensitive information
      const sanitizedData = dataValidationService.sanitizeBackupData(backupData);

      let dataToSave: string;
      let fileExtension = '.json';

      if (password) {
        // Encrypt the data if password provided
        dataToSave = await encryptionService.encryptDataCombined(
          JSON.stringify(sanitizedData, null, 2),
          password
        );
        fileExtension = '.encrypted';
      } else {
        dataToSave = JSON.stringify(sanitizedData, null, 2);
      }

      const filename = `godmode-backup-${new Date().toISOString().split('T')[0]}${fileExtension}`;

      const fileHandle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        types: [{
          description: password ? 'Encrypted GodMode Backup Files' : 'GodMode Backup Files',
          accept: password ? { 'application/octet-stream': ['.encrypted'] } : { 'application/json': ['.json'] }
        }]
      });

      const writable = await fileHandle.createWritable();
      await writable.write(dataToSave);
      await writable.close();

      this.lastBackupTime = new Date();
      this.saveBackupSettings();
      this.addToBackupHistory('local', true);

      console.log(`✅ Backup saved to local device${password ? ' (encrypted)' : ''}`);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('❌ Failed to save backup:', error);
        this.addToBackupHistory('local', false, error.message);
        throw error;
      }
    }
  }

  // Download backup as file (fallback method)
  private async downloadBackupFile(password?: string): Promise<void> {
    try {
      const backupData = this.createBackupData();

      // Sanitize backup data to remove sensitive information
      const sanitizedData = dataValidationService.sanitizeBackupData(backupData);

      let dataToSave: string;
      let mimeType = 'application/json';
      let fileExtension = '.json';

      if (password) {
        // Encrypt the data if password provided
        dataToSave = await encryptionService.encryptDataCombined(
          JSON.stringify(sanitizedData, null, 2),
          password
        );
        mimeType = 'application/octet-stream';
        fileExtension = '.encrypted';
      } else {
        dataToSave = JSON.stringify(sanitizedData, null, 2);
      }

      const blob = new Blob([dataToSave], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `godmode-backup-${new Date().toISOString().split('T')[0]}${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      this.lastBackupTime = new Date();
      this.saveBackupSettings();
      this.addToBackupHistory('local', true);

      console.log(`✅ Backup file downloaded${password ? ' (encrypted)' : ''}`);
    } catch (error: any) {
      console.error('❌ Failed to download backup:', error);
      this.addToBackupHistory('local', false, error.message);
      throw error;
    }
  }

  // Load backup from local device
  async loadFromLocalDevice(): Promise<void> {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';

    return new Promise((resolve, reject) => {
      input.onchange = async (event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) {
          reject(new Error('No file selected'));
          return;
        }

        try {
          const text = await file.text();
          const backupData = JSON.parse(text);
          await this.restoreFromBackup(backupData);
          resolve();
        } catch (error: any) {
          console.error('❌ Failed to restore backup:', error);
          reject(new Error(`Failed to restore backup: ${error.message}`));
        }
      };

      input.click();
    });
  }

  // Google Drive backup
  async backupToGoogleDrive(password?: string): Promise<void> {
    if (!simplifiedDriveService.isConfigured()) {
      throw new Error('Google Drive not configured. Please connect Google Drive first.');
    }

    try {
      const backupData = this.createBackupData();
      backupData.metadata.backupMethod = 'automatic';

      // Sanitize backup data
      const sanitizedData = dataValidationService.sanitizeBackupData(backupData);

      let dataToUpload: string;
      let filename: string;

      if (password) {
        // Encrypt the data if password provided
        dataToUpload = await encryptionService.encryptDataCombined(
          JSON.stringify(sanitizedData, null, 2),
          password
        );
        filename = `godmode-backup-${new Date().toISOString().split('T')[0]}.encrypted`;
      } else {
        dataToUpload = JSON.stringify(sanitizedData, null, 2);
        filename = `godmode-backup-${new Date().toISOString().split('T')[0]}.json`;
      }

      // Upload to Google Drive
      const fileId = await simplifiedDriveService.uploadBackupFile(filename, dataToUpload);

      this.lastBackupTime = new Date();
      this.saveBackupSettings();
      this.addToBackupHistory('google_drive', true);

      console.log(`✅ Backup uploaded to Google Drive${password ? ' (encrypted)' : ''}: ${fileId}`);

      // Clean up old backups (keep last 10)
      await this.cleanupOldGoogleDriveBackups();

    } catch (error: any) {
      console.error('❌ Google Drive backup failed:', error);
      this.addToBackupHistory('google_drive', false, error.message);
      throw error;
    }
  }

  private async cleanupOldGoogleDriveBackups(): Promise<void> {
    try {
      const files = await simplifiedDriveService.listBackupFiles();

      // Keep only the 10 most recent backups
      if (files.length > 10) {
        const filesToDelete = files.slice(10);

        for (const file of filesToDelete) {
          try {
            await simplifiedDriveService.deleteBackupFile(file.id);
            console.log(`🗑️ Deleted old backup: ${file.name}`);
          } catch (error) {
            console.warn(`⚠️ Failed to delete old backup ${file.name}:`, error);
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to cleanup old backups:', error);
    }
  }

  async loadFromGoogleDrive(): Promise<void> {
    if (!simplifiedDriveService.isConfigured()) {
      throw new Error('Google Drive not configured');
    }

    try {
      // Get list of backup files
      const files = await simplifiedDriveService.listBackupFiles();

      if (files.length === 0) {
        throw new Error('No backup files found in Google Drive');
      }

      // Use the most recent backup
      const latestBackup = files[0];

      // Download and restore
      const backupContent = await simplifiedDriveService.downloadBackupFile(latestBackup.id);

      let backupData: BackupData;

      if (latestBackup.name.endsWith('.encrypted')) {
        // Handle encrypted backup - would need password input from user
        throw new Error('Encrypted backup detected. Password-protected restore from Google Drive coming soon.');
      } else {
        backupData = JSON.parse(backupContent);
      }

      await this.restoreFromBackup(backupData);

    } catch (error: any) {
      console.error('❌ Google Drive restore failed:', error);
      throw error;
    }
  }

  // Auto-backup functionality
  enableAutoBackup(providerId: string, intervalHours: number = 24): void {
    if (!this.isProviderConfigured(providerId) || providerId === 'local') {
      throw new Error(`Provider ${providerId} not configured or doesn't support auto-backup`);
    }

    this.autoBackupEnabled = true;
    this.scheduleAutoBackup(providerId, intervalHours);
    this.saveBackupSettings();

    console.log(`✅ Auto-backup enabled for ${providerId} every ${intervalHours} hours`);
  }

  disableAutoBackup(): void {
    this.autoBackupEnabled = false;
    this.clearAutoBackupSchedule();
    this.saveBackupSettings();

    console.log('✅ Auto-backup disabled');
  }

  // Backup history and management
  getBackupHistory(): BackupHistoryEntry[] {
    const history = localStorage.getItem('godmode_backup_history');
    return history ? JSON.parse(history) : [];
  }

  addToBackupHistory(providerId: string, success: boolean, error?: string): void {
    const history = this.getBackupHistory();
    const entry: BackupHistoryEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      providerId,
      success,
      error,
      dataSize: JSON.stringify(this.createBackupData()).length
    };

    history.unshift(entry);

    // Keep only last 50 entries
    if (history.length > 50) {
      history.splice(50);
    }

    localStorage.setItem('godmode_backup_history', JSON.stringify(history));
  }

  getLastBackupTime(): Date | null {
    return this.lastBackupTime;
  }

  isAutoBackupEnabled(): boolean {
    return this.autoBackupEnabled;
  }

  // Data size calculation
  getDataSize(): number {
    return JSON.stringify(this.createBackupData()).length;
  }

  getDataSizeFormatted(): string {
    const bytes = this.getDataSize();
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Export formatted backup data for sharing/debugging
  exportDebugData(): string {
    const debugData = {
      config: {
        autoBackupEnabled: this.autoBackupEnabled,
        lastBackupTime: this.lastBackupTime?.toISOString(),
        dataSize: this.getDataSizeFormatted()
      },
      history: this.getBackupHistory().slice(0, 10), // Last 10 entries
      providers: this.getAvailableProviders(),
      timestamp: new Date().toISOString()
    };

    return JSON.stringify(debugData, null, 2);
  }

  // Private helper methods
  private getStorageItem(key: string): any {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  }

  private setStorageItem(key: string, value: any): void {
    if (value !== null && value !== undefined) {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }


  private getDeviceInfo(): string {
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    return `${platform} - ${ua.split(' ')[0]} - ${new Date().toISOString()}`;
  }

  private isProviderConfigured(providerId: string): boolean {
    if (providerId === 'local') return true;
    if (providerId === 'google_drive') return simplifiedDriveService.isConfigured();

    // Check for other provider configurations
    const configKey = `godmode_backup_${providerId}_config`;
    return localStorage.getItem(configKey) !== null;
  }

  private loadBackupSettings(): void {
    const settings = localStorage.getItem('godmode_backup_settings');
    if (settings) {
      try {
        const parsed = JSON.parse(settings);
        this.autoBackupEnabled = parsed.autoBackupEnabled || false;
        this.lastBackupTime = parsed.lastBackupTime ? new Date(parsed.lastBackupTime) : null;
      } catch (error) {
        console.error('❌ Failed to load backup settings:', error);
      }
    }
  }

  private saveBackupSettings(): void {
    const settings = {
      autoBackupEnabled: this.autoBackupEnabled,
      lastBackupTime: this.lastBackupTime?.toISOString()
    };
    localStorage.setItem('godmode_backup_settings', JSON.stringify(settings));
  }

  private initializeProviders(): void {
    // Initialize cloud storage providers
    // This would be implemented in separate provider classes
    console.log('🔧 Backup providers initialized');
  }

  private scheduleAutoBackup(providerId: string, intervalHours: number): void {
    // Clear existing schedule
    this.clearAutoBackupSchedule();

    // Schedule new backup
    this.autoBackupInterval = window.setInterval(async () => {
      try {
        console.log(`🔄 Running scheduled backup to ${providerId}`);

        switch (providerId) {
          case 'google_drive':
            await this.backupToGoogleDrive(); // No password for auto-backups
            break;
          default:
            console.warn(`⚠️ Auto-backup not implemented for ${providerId}`);
        }

        console.log(`✅ Scheduled backup to ${providerId} completed`);
      } catch (error) {
        console.error(`❌ Scheduled backup failed:`, error);
        this.addToBackupHistory(providerId, false, error instanceof Error ? error.message : 'Unknown error');

        // Disable auto-backup on repeated failures
        const recentFailures = this.getBackupHistory()
          .filter(h => h.providerId === providerId && !h.success)
          .slice(0, 3);

        if (recentFailures.length >= 3) {
          console.error('❌ Disabling auto-backup due to repeated failures');
          this.disableAutoBackup();
        }
      }
    }, intervalHours * 60 * 60 * 1000); // Convert hours to milliseconds

    console.log(`⏰ Auto-backup scheduled every ${intervalHours} hours`);
  }

  // Add method to enable auto-backup for Google Drive specifically
  enableGoogleDriveAutoBackup(intervalHours: number = 24): void {
    if (!simplifiedDriveService.isConfigured()) {
      throw new Error('Google Drive not configured');
    }

    this.enableAutoBackup('google_drive', intervalHours);
  }

  private clearAutoBackupSchedule(): void {
    if (this.autoBackupInterval) {
      clearInterval(this.autoBackupInterval);
      this.autoBackupInterval = null;
    }
  }
}

// Create singleton instance
export const dataBackupService = new DataBackupService();

// Expose debug utilities in development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).backupDebug = {
    // Get backup service instance
    service: dataBackupService,

    // Get current data size
    getDataSize: () => dataBackupService.getDataSizeFormatted(),

    // Get backup history
    getHistory: () => dataBackupService.getBackupHistory(),

    // Export debug data
    export: () => {
      const data = dataBackupService.exportDebugData();
      console.log('📋 Copy this data for debugging:');
      console.log(data);
      return data;
    },

    // Trigger manual backup test
    testBackup: () => dataBackupService.backupToLocalDevice(),

    // Get all localStorage keys
    getStorageKeys: () => {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('godmode_')) {
          keys.push(key);
        }
      }
      return keys;
    }
  };

  // console.log('🔧 Backup Debug utilities available:');
  // console.log('  backupDebug.getDataSize()  - Get current data size');
  // console.log('  backupDebug.getHistory()   - Get backup history');
  // console.log('  backupDebug.export()       - Export debug data');
  // console.log('  backupDebug.testBackup()   - Test backup download');
  // console.log('  backupDebug.getStorageKeys() - List all storage keys');
}